import { Typography } from '@mui/material';
import Button from '@mui/material/Button';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  COOKIE_CONTINUE_NO_UPDATE_BROWSER,
  getBrowserDownloadLink,
  getCookie,
  isBrowserSupported,
} from '@/utils/browser';

import {
  BankLogo,
  BrowserDetectorAction,
  BrowserDetectorContainer,
  BrowserDetectorContent,
  BrowserDetectorDialog,
  BrowserDetectorIcon,
} from './BrowserDetector.styled';

type BrowserDetectorProps = {
  children: React.ReactNode;
  fullScreen?: boolean;
};

export const BrowserDetector = ({
  children,
  fullScreen = false,
}: BrowserDetectorProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const userAgent = navigator.userAgent;
  const isSupported = isBrowserSupported(userAgent);
  const browserDownloadLink = getBrowserDownloadLink(userAgent);

  const handleContinue = () => {
    setOpen(false);
    document.cookie = `${COOKIE_CONTINUE_NO_UPDATE_BROWSER}=true`;
  };

  const handleRedirect = () => {
    window.location.href = browserDownloadLink;
  };

  useEffect(() => {
    const continueWithoutUpdate = getCookie(COOKIE_CONTINUE_NO_UPDATE_BROWSER);

    if (isSupported || continueWithoutUpdate === 'true') {
      setOpen(false);
    } else {
      setOpen(true);
    }
  }, [isSupported]);

  return (
    <>
      <BrowserDetectorDialog fullScreen={fullScreen} open={open}>
        <BrowserDetectorContainer>
          <BankLogo />
          <BrowserDetectorContent>
            <BrowserDetectorIcon />
            <center>
              <Typography variant="h3" gutterBottom>
                {t('browserDetector.title')}
              </Typography>
            </center>
            <Typography variant="body1">
              {t('browserDetector.description1')}
            </Typography>
            <Typography variant="body1">
              {t('browserDetector.description2')}
            </Typography>
            <Typography variant="body1Bold">
              {t('browserDetector.forMobileUsers.heading')}
            </Typography>
            <ul>
              <li>{t('browserDetector.forMobileUsers.safariMac')}</li>
              <li>{t('browserDetector.forMobileUsers.safariIOS')}</li>
              <li>{t('browserDetector.forMobileUsers.samsungInternet')}</li>
            </ul>
            <Typography variant="body1Bold">
              {t('browserDetector.forDesktopUsers.heading')}
            </Typography>
            <ul>
              <li>{t('browserDetector.forDesktopUsers.chrome')}</li>
              <li>{t('browserDetector.forDesktopUsers.edge')}</li>
              <li>{t('browserDetector.forDesktopUsers.firefox')}</li>
              <li>{t('browserDetector.forDesktopUsers.opera')}</li>
            </ul>
            <BrowserDetectorAction>
              <Button variant="outlined" onClick={handleContinue}>
                {t('common.continue')}
              </Button>
              <Button onClick={handleRedirect} disabled={!browserDownloadLink}>
                {t('browserDetector.button.update')}
              </Button>
            </BrowserDetectorAction>
          </BrowserDetectorContent>
        </BrowserDetectorContainer>
      </BrowserDetectorDialog>
      {children}
    </>
  );
};
